import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import { FormLoading } from '@/components/FormLoading';
import { ChatInput } from '@/components/roc/ChatInput';
import type { RocDocumentDownloadArgs } from '@/components/roc/DocumentMessageCard';
import { EXACARE_LOGOS_S3_BUCKET_URLS } from '@/constants';
import { useSnackbar } from '@/hooks/useSnackbar';
import { PostHog } from '@/lib/PostHog';
import ConversationMessagesPanel from '@/pages/CRM/ReferralOutbound/ReferralManagement/components/ConversationMessagesPanel';
import {
  DocumentsTab,
  type DocumentsTabDocument
} from '@/pages/CRM/ReferralOutbound/ReferralManagement/components/DocumentsTab';
import {
  type ReadState,
  shouldMarkAsRead
} from '@/pages/CRM/ReferralOutbound/ReferralManagement/lib/readReceipts';
import type { InvitationVerification } from '@/types/roc/models/InvitationVerification';
import type { ReadReceipt } from '@/types/roc/models/ReadReceipt';
import type { RecipientStatus } from '@/types/roc/models/RecipientStatus';
import { RecipientStatus as RecipientStatusEnum } from '@/types/roc/models/RecipientStatus';
import { ResponseError } from '@/types/roc/runtime';

import { ReferralJourneyStepper } from './components/ReferralJourneyStepper';
import { ReferralSummary } from './components/ReferralSummary';
import {
  getPublicReferralAccessErrorMessage,
  type PublicReferralAccessData,
  usePublicReferralAccess
} from './hooks/usePublicReferralAccess';
import {
  usePublicReferralConversation,
  usePublicReferralMessages,
  usePublicSendReferralMessageMutation
} from './hooks/usePublicReferralMessages';
import { formatDateShort, getReferralTypeLabel } from './lib/referralDisplay';

import { invitationVerificationsAtom } from './invitationVerificationAtom';
import { PasscodeGate } from './PasscodeGate';
import { PublicReferralAttachDialog } from './PublicReferralAttachDialog';
import {
  getPublicReferralAccessQueryKey,
  getPublicReferralDocumentsQueryKey,
  getPublicReferralMessagesQueryKey
} from './queryKeys';

const STATUS_ACTIONS: Array<{ label: string; status: RecipientStatus }> = [
  { label: 'Accept', status: RecipientStatusEnum.Accepted },
  { label: 'Decline', status: RecipientStatusEnum.Declined },
  { label: 'Need More Info', status: RecipientStatusEnum.NeedsMoreInfo }
];

const PREVIEWABLE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

function isPreviewableDocument(fileName: string) {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? PREVIEWABLE_EXTENSIONS.includes(extension) : false;
}

function getMessageFromError(error: unknown, fallback: string) {
  if (error instanceof ResponseError) {
    if (error.response.status === 401 || error.response.status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
  }

  return fallback;
}

function isAuthorizationError(error: unknown) {
  if (!(error instanceof ResponseError)) {
    return false;
  }

  return error.response.status === 401 || error.response.status === 403;
}

function isClosedReferralError(error: unknown) {
  return error instanceof ResponseError && error.response.status === 410;
}

function computePublicReadState(
  messageId: string,
  readReceipts: ReadReceipt[] | undefined,
  senderFacilityId: string | undefined,
  recipientFacilityId: string | undefined
): ReadState | null {
  if (!readReceipts || !senderFacilityId || !recipientFacilityId) {
    return { firstReadAt: null };
  }

  const eligible = readReceipts.filter(
    (receipt) =>
      receipt.user_facility_id === senderFacilityId &&
      receipt.recipient_facility_id === recipientFacilityId &&
      receipt.last_read_message_id >= messageId
  );

  if (eligible.length === 0) {
    return { firstReadAt: null };
  }

  const firstReadAt = eligible.map((receipt) => receipt.last_read_at).sort()[0];
  return { firstReadAt: firstReadAt ?? null };
}

export default function PublicReferralAccessPage() {
  const { token: invitationToken } = useParams<{ token: string }>();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState<RecipientStatus | null>(
    null
  );
  const [chatInputKey, setChatInputKey] = useState(0);
  const [invitationVerifications, setInvitationVerifications] = useAtom(
    invitationVerificationsAtom
  );
  const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
    fileName: string;
    documentId: string;
  } | null>(null);
  const documentListRef = useRef<HTMLDivElement | null>(null);
  const [canScrollDocumentList, setCanScrollDocumentList] = useState(false);
  const [isDocListOpen, setIsDocListOpen] = useState(false);
  // Below `md`, documents come first but stay collapsed while messages start
  // expanded, so the recipient can read the conversation without tapping.
  const [isMessagesOpen, setIsMessagesOpen] = useState(true);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMessagesVisible = !isNarrowScreen || isMessagesOpen;

  const updateDocumentListScrollHint = (element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }
    setCanScrollDocumentList(
      element.scrollTop + element.clientHeight < element.scrollHeight - 4
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure the scroll hint when the preview opens or the phone doc list toggles.
  useEffect(() => {
    updateDocumentListScrollHint(documentListRef.current);
  }, [previewDocument, isDocListOpen]);
  const latestAccessQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralAccessQueryKey
  > | null>(null);
  const latestMessagesQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralMessagesQueryKey
  > | null>(null);
  const latestDocumentsQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralDocumentsQueryKey
  > | null>(null);

  const verifyInvitationMutation = useMutation({
    mutationFn: (passcode: string) => {
      if (!invitationToken) {
        throw new Error('Missing referral access token.');
      }

      const client = createPublicRocClient();
      return client.verifyInvitation({
        token: invitationToken,
        verifyInvitationRequest: { passcode }
      });
    },
    onSuccess: (data) => {
      if (invitationToken) {
        setInvitationVerifications((current) => ({
          ...current,
          [invitationToken]: data
        }));
      }
    }
  });

  const resendPasscodeMutation = useMutation({
    mutationFn: () => {
      if (!invitationToken) {
        throw new Error('Missing referral access token.');
      }

      const client = createPublicRocClient();
      return client.resendInvitationPasscode({ token: invitationToken });
    }
  });

  useEffect(() => {
    if (!invitationToken) {
      return;
    }
    verifyInvitationMutation.reset();
    resendPasscodeMutation.reset();
  }, [
    invitationToken,
    resendPasscodeMutation.reset,
    verifyInvitationMutation.reset
  ]);

  const verification: InvitationVerification | null = invitationToken
    ? (invitationVerifications[invitationToken] ?? null)
    : null;
  const sessionToken = verification?.session_token;
  const referralIdFromVerification = verification?.referral_id;
  const accessQuery = usePublicReferralAccess(
    invitationToken,
    sessionToken,
    referralIdFromVerification
  );
  const referralId = accessQuery.data?.referral.referral_id;
  const recipientFacilityId = accessQuery.data?.referral.recipient.facility_id;
  const senderFacilityId = accessQuery.data?.referral.sender_facility_id;

  const messagesQuery = usePublicReferralMessages(
    invitationToken,
    sessionToken,
    referralId
  );
  const sendMessageMutation = usePublicSendReferralMessageMutation(
    invitationToken,
    sessionToken,
    referralId
  );
  const accessQueryKey = useMemo(
    () => getPublicReferralAccessQueryKey(invitationToken, sessionToken),
    [invitationToken, sessionToken]
  );
  const messagesQueryKey = useMemo(
    () =>
      getPublicReferralMessagesQueryKey(
        invitationToken,
        sessionToken,
        referralId
      ),
    [invitationToken, sessionToken, referralId]
  );
  const documentsQueryKey = useMemo(
    () =>
      getPublicReferralDocumentsQueryKey(
        invitationToken,
        sessionToken,
        referralId
      ),
    [invitationToken, sessionToken, referralId]
  );

  latestAccessQueryKeyRef.current = accessQueryKey;
  latestMessagesQueryKeyRef.current = messagesQueryKey;
  latestDocumentsQueryKeyRef.current = documentsQueryKey;

  const documentsQuery = useQuery({
    queryKey: documentsQueryKey,
    queryFn: async () => {
      if (!invitationToken || !sessionToken || !referralId) {
        throw new Error('Missing referral context.');
      }

      const client = createPublicRocClient(sessionToken, invitationToken);
      const response = await client.listDocuments({
        referralId,
        limit: 100
      });

      return response.items;
    },
    enabled: Boolean(invitationToken && sessionToken && referralId),
    retry: false
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: RecipientStatus) => {
      if (!invitationToken || !sessionToken || !referralId) {
        throw new Error('Missing referral context.');
      }

      const client = createPublicRocClient(sessionToken, invitationToken);
      return client.patchRecipientStatus({
        referralId,
        updateRecipientStatusRequest: { status }
      });
    },
    onSuccess: (recipient) => {
      queryClient.setQueryData(
        accessQueryKey,
        (current: PublicReferralAccessData | undefined) =>
          current
            ? {
                ...current,
                referral: {
                  ...current.referral,
                  recipient: {
                    ...current.referral.recipient,
                    status: recipient.status
                  }
                }
              }
            : current
      );

      queryClient.invalidateQueries({
        queryKey: messagesQueryKey
      });
    }
  });

  const markAsReadMutation = useMutation({
    mutationFn: ({
      referralId,
      recipientFacilityId,
      lastReadMessageId
    }: {
      referralId: string;
      recipientFacilityId: string;
      lastReadMessageId: string;
    }) => {
      if (!invitationToken || !sessionToken) {
        throw new Error('Missing verified referral access.');
      }

      const client = createPublicRocClient(sessionToken, invitationToken);
      return client.upsertReadReceipt({
        referralId,
        recipientFacilityId,
        updateReadReceiptRequest: {
          last_read_message_id: lastReadMessageId
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagesQueryKey
      });
    }
  });

  const conversationMessages = usePublicReferralConversation(
    messagesQuery.data?.messages,
    senderFacilityId,
    recipientFacilityId
  );

  const latestRecipientSentMessageId = useMemo(() => {
    if (!recipientFacilityId) {
      return null;
    }

    for (let index = conversationMessages.length - 1; index >= 0; index -= 1) {
      const message = conversationMessages[index];
      if (
        message &&
        !message.isSystemEvent() &&
        message.receiver_facility_id &&
        message.sender_facility_id === recipientFacilityId
      ) {
        return message.message_id;
      }
    }

    return null;
  }, [conversationMessages, recipientFacilityId]);

  const latestConversationMessage = useMemo(
    () =>
      conversationMessages.length > 0
        ? (conversationMessages[conversationMessages.length - 1] ?? null)
        : null,
    [conversationMessages]
  );
  const markAsRead = markAsReadMutation.mutate;

  useEffect(() => {
    return () => {
      const latestAccessQueryKey = latestAccessQueryKeyRef.current;
      const latestMessagesQueryKey = latestMessagesQueryKeyRef.current;
      const latestDocumentsQueryKey = latestDocumentsQueryKeyRef.current;

      if (latestAccessQueryKey) {
        queryClient.removeQueries({
          queryKey: latestAccessQueryKey,
          exact: true
        });
      }

      if (latestMessagesQueryKey) {
        queryClient.removeQueries({
          queryKey: latestMessagesQueryKey,
          exact: true
        });
      }

      if (latestDocumentsQueryKey) {
        queryClient.removeQueries({
          queryKey: latestDocumentsQueryKey,
          exact: true
        });
      }
    };
  }, [queryClient]);

  useEffect(() => {
    if (!isMessagesVisible || !latestConversationMessage || !referralId) {
      return;
    }

    if (latestConversationMessage.sender_facility_id === recipientFacilityId) {
      return;
    }

    if (
      !shouldMarkAsRead(
        latestConversationMessage.message_id,
        messagesQuery.data?.readReceipts ?? [],
        recipientFacilityId ?? '',
        recipientFacilityId ?? ''
      )
    ) {
      return;
    }

    if (!recipientFacilityId) {
      return;
    }

    markAsRead({
      referralId,
      recipientFacilityId,
      lastReadMessageId: latestConversationMessage.message_id
    });
  }, [
    isMessagesVisible,
    latestConversationMessage,
    messagesQuery.data?.readReceipts,
    markAsRead,
    recipientFacilityId,
    referralId
  ]);

  const handleDownloadDocument = async ({
    referralId: downloadReferralId,
    documentId,
    fileName
  }: RocDocumentDownloadArgs) => {
    if (!invitationToken || !sessionToken) {
      throw new Error('Missing verified referral access.');
    }

    PostHog.captureScreenerAction('external-referral-document-open', {
      referral_id: downloadReferralId,
      document_id: documentId,
      mode: isPreviewableDocument(fileName) ? 'preview' : 'download'
    });

    const client = createPublicRocClient(sessionToken, invitationToken);
    const { url } = await client.getDocumentDownloadUrl({
      referralId: downloadReferralId,
      documentId
    });
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error('Unexpected download URL protocol');
    }

    if (isPreviewableDocument(fileName)) {
      setPreviewDocument({ url: parsed.toString(), fileName, documentId });
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = parsed.toString();
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const handleSelectDocument = async (doc: DocumentsTabDocument) => {
    try {
      await handleDownloadDocument({
        referralId: doc.referral_id,
        documentId: doc.document_id,
        fileName: doc.file_name
      });
      if (isPhone) {
        setIsDocListOpen(false);
      }
    } catch (error) {
      showSnackbar({
        message: getMessageFromError(error, 'Failed to open document.'),
        severity: 'error'
      });
    }
  };

  const currentFacilityName =
    accessQuery.data?.referral.recipient.facility_name ?? 'Your facility';

  const partnerFacilityName =
    accessQuery.data?.referral.sender_facility_name ?? 'Sender facility';

  const partnerLabelMap = useMemo(
    () => ({
      [senderFacilityId ?? '']: partnerFacilityName,
      [recipientFacilityId ?? '']: currentFacilityName
    }),
    [
      currentFacilityName,
      partnerFacilityName,
      recipientFacilityId,
      senderFacilityId
    ]
  );
  const publicDocuments = documentsQuery.data as
    | DocumentsTabDocument[]
    | undefined;
  const documentsCount =
    documentsQuery.isLoading || documentsQuery.isError
      ? null
      : (publicDocuments?.length ?? 0);
  const canReverify =
    verification &&
    accessQuery.isError &&
    isAuthorizationError(accessQuery.error) &&
    dayjs(verification.expires_at).isAfter(dayjs());

  useEffect(() => {
    if (canReverify && invitationToken) {
      setInvitationVerifications((current) => {
        if (!(invitationToken in current)) {
          return current;
        }
        const { [invitationToken]: _removed, ...rest } = current;
        return rest;
      });
    }
  }, [canReverify, invitationToken, setInvitationVerifications]);
  const isReferralUnavailable = isClosedReferralError(
    verifyInvitationMutation.error
  );
  let passcodeErrorMessage: string | null = null;
  if (verifyInvitationMutation.isError) {
    passcodeErrorMessage = getPublicReferralAccessErrorMessage(
      verifyInvitationMutation.error,
      'Unable to verify this passcode.'
    );
  } else if (canReverify) {
    passcodeErrorMessage =
      'Your session is no longer valid. Enter the passcode to continue.';
  }
  const handleUploadedDocuments = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: messagesQueryKey
      }),
      queryClient.invalidateQueries({
        queryKey: documentsQueryKey
      })
    ]);
  };
  const readStateByMessageId = useMemo(
    () =>
      Object.fromEntries(
        conversationMessages.map((message) => [
          message.message_id,
          message.message_id === latestRecipientSentMessageId
            ? computePublicReadState(
                message.message_id,
                messagesQuery.data?.readReceipts,
                senderFacilityId,
                recipientFacilityId
              )
            : null
        ])
      ) as Record<string, ReadState | null>,
    [
      conversationMessages,
      latestRecipientSentMessageId,
      messagesQuery.data?.readReceipts,
      recipientFacilityId,
      senderFacilityId
    ]
  );

  if (isReferralUnavailable) {
    return (
      <Stack
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        px={2}
        textAlign="center"
        gap={1}
      >
        <Typography variant="h6">
          This referral is no longer available
        </Typography>
        <Typography color="text.secondary">
          This referral has been closed. Please contact the sender if it needs
          to be reopened.
        </Typography>
      </Stack>
    );
  }

  if (!verification || canReverify) {
    return (
      <PasscodeGate
        key={invitationToken}
        errorMessage={passcodeErrorMessage}
        hasResent={resendPasscodeMutation.isSuccess}
        isResending={resendPasscodeMutation.isLoading}
        isSubmitting={verifyInvitationMutation.isLoading}
        resendMessage={
          resendPasscodeMutation.isSuccess
            ? 'If you do not receive an email within a few minutes, check your spam folder or contact the referral sender.'
            : null
        }
        onResend={() => resendPasscodeMutation.mutate()}
        onSubmit={(passcode) => {
          PostHog.captureScreenerAction('external-referral-passcode-submit');
          verifyInvitationMutation.mutate(passcode);
        }}
      />
    );
  }

  if (accessQuery.isLoading) {
    return <FormLoading label="Loading referral…" subLabel="" />;
  }

  if (accessQuery.isError || !accessQuery.data) {
    return (
      <Stack
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        px={2}
        textAlign="center"
        gap={1}
      >
        <Typography variant="h6">Unable to open this referral</Typography>
        <Typography color="text.secondary">
          {getPublicReferralAccessErrorMessage(
            accessQuery.error,
            'Failed to load this referral.'
          )}
        </Typography>
      </Stack>
    );
  }

  const { referral } = accessQuery.data;

  const messagesPanel = (
    <Stack
      key="messages"
      flex={1}
      sx={{
        minWidth: 0,
        minHeight: { xs: isMessagesOpen ? 420 : 0, md: 0 },
        position: 'relative'
      }}
    >
      {(sendMessageMutation.isLoading || updateStatusMutation.isLoading) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1
          }}
        />
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (isNarrowScreen) {
            setIsMessagesOpen((prev) => !prev);
          }
        }}
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.300',
          cursor: { xs: 'pointer', md: 'default' }
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: 'text.secondary',
            textTransform: 'uppercase'
          }}
        >
          Messages
        </Typography>
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            display: { xs: 'block', md: 'none' },
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: isMessagesOpen ? 'rotate(180deg)' : 'none'
          }}
        />
      </Stack>

      <Box
        sx={{
          display: { xs: isMessagesOpen ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <ConversationMessagesPanel
          messages={conversationMessages}
          resetKey={`${referralId ?? ''}:${recipientFacilityId ?? ''}`}
          senderFacilityId={recipientFacilityId ?? ''}
          senderFacilityName={currentFacilityName}
          getFacilityName={(facilityId) =>
            partnerLabelMap[facilityId] ?? facilityId
          }
          readStateByMessageId={readStateByMessageId}
          onOpenDocument={handleDownloadDocument}
          isLoading={messagesQuery.isLoading}
          isError={messagesQuery.isError}
        />

        <ChatInput
          key={chatInputKey}
          onSend={async (content) => {
            PostHog.captureScreenerAction(
              'external-referral-chat-message-send',
              { referral_id: referralId }
            );
            try {
              await sendMessageMutation.mutateAsync(content);
              setChatInputKey((current) => current + 1);
            } catch (error) {
              showSnackbar({
                message: getMessageFromError(
                  error,
                  'Failed to send message. Please try again.'
                ),
                severity: 'error'
              });
            }
          }}
          isSending={sendMessageMutation.isLoading}
          onAttach={() => setIsAttachDialogOpen(true)}
          suppressAutocapture
        />
      </Box>
    </Stack>
  );

  const documentsPanel = (
    <Box
      key="documents"
      sx={(theme) => ({
        width: { xs: '100%', md: 384 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderBottom: {
          xs: `1px solid ${theme.palette.grey[300]}`,
          md: 'none'
        },
        borderLeft: { md: `1px solid ${theme.palette.grey[300]}` }
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (isNarrowScreen) {
            setIsDocumentsOpen((prev) => !prev);
          }
        }}
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          cursor: { xs: 'pointer', md: 'default' }
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: 'text.secondary',
            textTransform: 'uppercase'
          }}
        >
          Documents
          {documentsCount !== null ? ` (${documentsCount})` : ''}
        </Typography>
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            display: { xs: 'block', md: 'none' },
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: isDocumentsOpen ? 'rotate(180deg)' : 'none'
          }}
        />
      </Stack>
      <Box
        sx={{
          display: {
            xs: isDocumentsOpen ? 'flex' : 'none',
            md: 'flex'
          },
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <DocumentsTab
          referralId={referralId ?? ''}
          documents={publicDocuments ?? []}
          isLoading={documentsQuery.isLoading}
          isError={documentsQuery.isError}
          onOpenDocument={handleDownloadDocument}
          actionVariant="open"
          suppressAutocapture
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: { md: '100vh' },
        bgcolor: 'grey.50',
        overflow: { xs: 'visible', md: 'hidden' }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.25
        }}
      >
        <CardMedia
          component="img"
          src={EXACARE_LOGOS_S3_BUCKET_URLS.fullColored}
          alt="ExaCare"
          sx={{ width: 'auto', height: 30, objectFit: 'contain' }}
        />
      </Stack>
      <Box
        sx={{
          px: { xs: 0, sm: 2, md: 3 },
          pb: { xs: 0, sm: 2, md: 3 }
        }}
      >
        <Stack
          sx={{
            width: '100%',
            height: {
              xs: 'auto',
              md: 'calc(100vh - 74px)'
            },
            bgcolor: 'background.paper',
            borderRadius: { xs: 0, sm: 1.5 },
            border: '1px solid',
            borderColor: 'grey.300',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <ReferralSummary
              patientName={
                [referral.first_name, referral.last_name]
                  .filter(Boolean)
                  .join(' ') || 'Patient'
              }
              dob={referral.dob}
              senderFacilityName={partnerFacilityName}
              referredForLabel={getReferralTypeLabel(referral.referral_type)}
              dischargeDate={formatDateShort(referral.target_discharge_date)}
              status={referral.recipient.status}
              disabled={updateStatusMutation.isLoading}
              onSelect={(status) => setPendingStatus(status)}
            />
          </Box>

          <Divider sx={{ borderColor: 'grey.300' }} />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            flex={1}
            sx={{ minHeight: 0 }}
          >
            {!isNarrowScreen && (
              <Box
                sx={(theme) => ({
                  width: 208,
                  flexShrink: 0,
                  px: 3,
                  py: 3,
                  borderRight: `1px solid ${theme.palette.grey[300]}`
                })}
              >
                <ReferralJourneyStepper status={referral.recipient.status} />
              </Box>
            )}

            {isNarrowScreen
              ? [documentsPanel, messagesPanel]
              : [messagesPanel, documentsPanel]}
          </Stack>
        </Stack>
      </Box>

      {invitationToken && sessionToken && referralId && (
        <PublicReferralAttachDialog
          open={isAttachDialogOpen}
          onClose={() => setIsAttachDialogOpen(false)}
          invitationToken={invitationToken}
          sessionToken={sessionToken}
          referralId={referralId}
          onUploaded={handleUploadedDocuments}
        />
      )}

      <Dialog
        open={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update this referral to{' '}
            {pendingStatus
              ? (STATUS_ACTIONS.find(
                  (action) => action.status === pendingStatus
                )?.label ?? pendingStatus)
              : ''}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            className="ph-no-capture"
            disabled={!pendingStatus || updateStatusMutation.isLoading}
            onClick={async () => {
              if (!pendingStatus) {
                return;
              }

              PostHog.captureScreenerAction('external-referral-status-submit', {
                status: pendingStatus
              });

              try {
                await updateStatusMutation.mutateAsync(pendingStatus);
                setPendingStatus(null);
              } catch (error) {
                showSnackbar({
                  message: getMessageFromError(
                    error,
                    'Failed to update referral status.'
                  ),
                  severity: 'error'
                });
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewDocument !== null}
        onClose={() => setPreviewDocument(null)}
        maxWidth={false}
        PaperProps={{
          sx: { width: '95vw', height: '95vh', maxWidth: 'none', m: 0 }
        }}
      >
        {previewDocument && (
          <>
            <DialogTitle
              sx={{
                fontSize: 16,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden' }}>
                {previewDocument.fileName}
              </Box>
              <Stack direction="row" gap={1} flexShrink={0}>
                <Button
                  size="small"
                  onClick={() => setIsDocListOpen((prev) => !prev)}
                  sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                >
                  {isDocListOpen ? 'Hide docs' : 'All docs'}
                </Button>
                <Button
                  size="small"
                  component="a"
                  href={previewDocument.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </Button>
                <Button size="small" onClick={() => setPreviewDocument(null)}>
                  Close
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{ p: 0, minHeight: 0, display: 'flex', flexDirection: 'row' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '100%', sm: 240 },
                  flexShrink: 0,
                  minHeight: 0,
                  display: {
                    xs: isDocListOpen ? 'flex' : 'none',
                    sm: 'flex'
                  },
                  borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`
                }}
              >
                <Stack
                  ref={documentListRef}
                  onScroll={(event) =>
                    updateDocumentListScrollHint(event.currentTarget)
                  }
                  sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
                >
                  {(publicDocuments ?? []).map((doc) => {
                    const isActive =
                      doc.document_id === previewDocument.documentId;
                    return (
                      <Box
                        key={doc.document_id}
                        onClick={() => handleSelectDocument(doc)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? 'primary.main' : 'text.primary',
                          bgcolor: isActive ? 'primary.light' : 'transparent',
                          borderLeft: '3px solid',
                          borderLeftColor: isActive
                            ? 'primary.main'
                            : 'transparent',
                          wordBreak: 'break-word',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {doc.file_name}
                      </Box>
                    );
                  })}
                </Stack>
                {canScrollDocumentList && (
                  <Stack
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 44,
                      pb: 0.5,
                      pointerEvents: 'none',
                      background: (theme) =>
                        `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`
                    }}
                  >
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      sx={{ color: 'text.secondary' }}
                    />
                  </Stack>
                )}
              </Box>
              <Box
                component="iframe"
                src={previewDocument.url}
                title={previewDocument.fileName}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  alignSelf: 'stretch',
                  border: 'none'
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
